/**
 * ReportsView — JMBR / SIB Regulatory PDF Reports
 *
 * Generates PDFs via the report-engine service (port 8003, proxied at /reports-api/).
 * Flow: POST trigger → 202 Accepted → poll GET /:id every 2 s → auto-download when ready.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useTheme } from '../hooks/useTheme'
import { REPORTS_API } from '../App'

// ─── Report catalogue ─────────────────────────────────────────────────────────

interface ReportDef {
  id: string
  endpoint: string
  label: string
  sublabel: string
  icon: string
  color: string
  body: Record<string, unknown>
  format: 'PDF' | 'XLSX'
}

const REPORT_DEFS: ReportDef[] = [
  {
    id: 'daily-availability',
    endpoint: '/reports/daily-availability',
    label: 'Disponibilidad Diaria',
    sublabel: 'Resumen ejecutivo de flota · KPIs + errores top',
    icon: '📊',
    color: '#22c55e',
    body: { zone: 'ALL', include_charts: true },
    format: 'PDF',
  },
  {
    id: 'zone-availability',
    endpoint: '/reports/zone-availability',
    label: 'Disponibilidad por Zona',
    sublabel: 'Desglose por zona con trend diario · SIB art. 12',
    icon: '🗺️',
    color: '#02AFF0',
    body: { period_days: 30 },
    format: 'PDF',
  },
  {
    id: 'indisponibilidad-historica',
    endpoint: '/reports/indisponibilidad-historica',
    label: 'Histórico de Indisponibilidad',
    sublabel: 'Log de incidentes + ATMs recurrentes · SIB art. 14',
    icon: '📉',
    color: '#f97316',
    body: { period_days: 30, top_n: 40 },
    format: 'PDF',
  },
  {
    id: 'analisis-incidentes',
    endpoint: '/reports/analisis-incidentes',
    label: 'Análisis de Incidentes',
    sublabel: 'Severidad · SLA · componentes · top errores',
    icon: '🔬',
    color: '#ef4444',
    body: { period_days: 30 },
    format: 'PDF',
  },
  {
    id: 'resumen-operativo',
    endpoint: '/reports/resumen-operativo',
    label: 'Resumen Operativo 24/7',
    sublabel: 'NOC + CIT + transacciones · turnos · SIB art. 18',
    icon: '🛡️',
    color: '#a855f7',
    body: { period_days: 7 },
    format: 'PDF',
  },
  {
    id: 'zone-health',
    endpoint: '/reports/zone-health',
    label: 'Zone Health Matrix',
    sublabel: 'Hoja Excel con métricas por zona · 7 días',
    icon: '📋',
    color: '#F89421',
    body: { zones: ['Distrito Nacional', 'Norte', 'Sur', 'Este'], period_days: 7 },
    format: 'XLSX',
  },
]

// ─── Types ────────────────────────────────────────────────────────────────────

type JobStatus = 'idle' | 'pending' | 'ready' | 'error'

interface Job {
  report_id: string
  defId: string
  label: string
  status: JobStatus
  filename?: string
  poll_url?: string
  error?: string
  started_at: number
  elapsed?: number
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Spinner() {
  return (
    <span style={{
      display: 'inline-block', width: 14, height: 14,
      border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
      borderRadius: '50%', animation: 'spin 0.7s linear infinite',
    }} />
  )
}

function StatusBadge({ status }: { status: JobStatus }) {
  const C = useTheme()
  const map: Record<JobStatus, { label: string; bg: string; color: string }> = {
    idle:    { label: '—',         bg: C.tint,             color: C.muted  },
    pending: { label: 'Generando', bg: 'rgba(248,148,33,.18)', color: '#F89421' },
    ready:   { label: 'Listo ✓',  bg: 'rgba(34,197,94,.18)', color: '#22c55e' },
    error:   { label: 'Error',     bg: 'rgba(239,68,68,.18)', color: '#ef4444' },
  }
  const s = map[status]
  return (
    <span style={{
      background: s.bg, color: s.color,
      padding: '2px 9px', borderRadius: 5,
      fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
    }}>
      {status === 'pending' ? <><Spinner /> &nbsp;{s.label}</> : s.label}
    </span>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ReportsView() {
  const C = useTheme()
  const [jobs, setJobs] = useState<Record<string, Job>>({})   // keyed by defId (one active per type)
  const [recentList, setRecentList] = useState<{ report_id: string; type: string; status: string; created_at: string }[]>([])
  const [engineOk, setEngineOk] = useState<boolean | null>(null)
  const pollRef = useRef<Record<string, ReturnType<typeof setInterval>>>({})

  // ── Health check ────────────────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${REPORTS_API}/health`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(() => setEngineOk(true))
      .catch(() => setEngineOk(false))
  }, [])

  // ── Fetch recent reports list ────────────────────────────────────────────────
  const refreshList = useCallback(() => {
    fetch(`${REPORTS_API}/reports`)
      .then(r => r.json())
      .then(d => setRecentList((d.reports ?? []).slice().reverse().slice(0, 20)))
      .catch(() => {})
  }, [])

  useEffect(() => { refreshList() }, [refreshList])

  // ── Cleanup polls on unmount ─────────────────────────────────────────────────
  useEffect(() => {
    return () => { Object.values(pollRef.current).forEach(clearInterval) }
  }, [])

  // ── Trigger report generation ────────────────────────────────────────────────
  async function triggerReport(def: ReportDef) {
    if (jobs[def.id]?.status === 'pending') return

    setJobs(prev => ({
      ...prev,
      [def.id]: { report_id: '', defId: def.id, label: def.label, status: 'pending', started_at: Date.now() },
    }))

    try {
      const res = await fetch(`${REPORTS_API}${def.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...def.body, date: new Date().toISOString().slice(0, 10) }),
      })
      const data = await res.json()

      if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`)

      const { report_id, poll_url } = data as { report_id: string; poll_url: string }

      setJobs(prev => ({
        ...prev,
        [def.id]: { ...prev[def.id], report_id, poll_url, started_at: Date.now() },
      }))

      // Poll every 2 s
      const iv = setInterval(async () => {
        try {
          const pr = await fetch(`${REPORTS_API}${poll_url}`, { method: 'HEAD' })

          if (pr.status === 200) {
            clearInterval(iv)
            delete pollRef.current[def.id]

            const elapsed = Math.round((Date.now() - (jobs[def.id]?.started_at ?? Date.now())) / 1000)

            setJobs(prev => ({
              ...prev,
              [def.id]: {
                ...prev[def.id],
                status: 'ready',
                elapsed,
                filename: pr.headers.get('Content-Disposition')?.match(/filename="(.+?)"/)?.[1],
              },
            }))
            refreshList()

          } else if (pr.status === 500) {
            clearInterval(iv)
            delete pollRef.current[def.id]
            setJobs(prev => ({ ...prev, [def.id]: { ...prev[def.id], status: 'error', error: 'El servidor reportó un error.' } }))
          }
        } catch { /* network hiccup, keep polling */ }
      }, 2000)

      pollRef.current[def.id] = iv

    } catch (err) {
      setJobs(prev => ({
        ...prev,
        [def.id]: { ...prev[def.id], status: 'error', error: err instanceof Error ? err.message : 'Error desconocido' },
      }))
    }
  }

  // ── Download ──────────────────────────────────────────────────────────────────
  function downloadReport(job: Job) {
    if (!job.poll_url) return
    const a = document.createElement('a')
    a.href = `${REPORTS_API}${job.poll_url}`
    a.download = job.filename ?? `report-${job.report_id.slice(0, 8)}.pdf`
    a.click()
  }

  // ── Styles ────────────────────────────────────────────────────────────────────
  const card: React.CSSProperties = {
    background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
    padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10,
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 14, background: C.dark, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>

      {/* ── Header ── */}
      <div style={{ ...card, flexDirection: 'row', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 10, flexShrink: 0,
          background: 'linear-gradient(135deg,#264E72,#02AFF0)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
        }}>📋</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>Reportes Regulatorios JMBR</div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>
            Generación de PDFs para remisión SIB · Banreservas Red de 933 Cajeros · Puppeteer + Handlebars
          </div>
        </div>

        {/* Engine health badge */}
        <div style={{
          padding: '6px 14px', borderRadius: 8, fontSize: 11, fontWeight: 700,
          background: engineOk === null ? C.tint : engineOk ? 'rgba(34,197,94,.15)' : 'rgba(239,68,68,.15)',
          color: engineOk === null ? C.muted : engineOk ? '#22c55e' : '#ef4444',
          border: `1px solid ${engineOk === null ? C.border : engineOk ? 'rgba(34,197,94,.3)' : 'rgba(239,68,68,.3)'}`,
          display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
        }}>
          {engineOk === null ? '⏳ Conectando…' : engineOk ? '● Report engine OK' : '✕ Engine no disponible'}
        </div>
      </div>

      {/* ── Engine offline banner ── */}
      {engineOk === false && (
        <div style={{
          background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)',
          borderRadius: 10, padding: '12px 16px', color: '#fca5a5', fontSize: 12, lineHeight: 1.6,
        }}>
          <strong>Report engine no accesible</strong> en <code style={{ background: 'rgba(0,0,0,.3)', padding: '1px 5px', borderRadius: 3 }}>{REPORTS_API}</code>.
          Verifica que el contenedor <code style={{ background: 'rgba(0,0,0,.3)', padding: '1px 5px', borderRadius: 3 }}>atmvision-reports</code> está corriendo
          y que nginx proxy <code style={{ background: 'rgba(0,0,0,.3)', padding: '1px 5px', borderRadius: 3 }}>/reports-api/</code> está activo.
        </div>
      )}

      {/* ── Report cards grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 12 }}>
        {REPORT_DEFS.map(def => {
          const job = jobs[def.id]
          const isGenerating = job?.status === 'pending'
          const isReady = job?.status === 'ready'
          const isError = job?.status === 'error'

          return (
            <div key={def.id} style={{
              ...card,
              borderLeft: `3px solid ${def.color}`,
              opacity: engineOk === false ? 0.5 : 1,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                  background: `${def.color}18`, border: `1px solid ${def.color}40`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17,
                }}>
                  {def.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text }}>{def.label}</div>
                  <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>{def.sublabel}</div>
                </div>
                <span style={{
                  fontSize: 9.5, fontWeight: 700, color: def.color,
                  background: `${def.color}18`, border: `1px solid ${def.color}40`,
                  padding: '2px 7px', borderRadius: 5, flexShrink: 0,
                }}>
                  {def.format}
                </span>
              </div>

              {/* Status row */}
              {job && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 6, borderTop: `1px solid ${C.border}` }}>
                  <StatusBadge status={job.status} />
                  {isReady && job.elapsed !== undefined && (
                    <span style={{ fontSize: 10, color: C.muted }}>{job.elapsed}s</span>
                  )}
                  {isError && (
                    <span style={{ fontSize: 10, color: '#ef4444', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {job.error}
                    </span>
                  )}
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                <button
                  disabled={isGenerating || engineOk === false}
                  onClick={() => triggerReport(def)}
                  style={{
                    flex: 1, padding: '8px 0', borderRadius: 7, border: 'none',
                    background: isGenerating ? C.tint : def.color,
                    color: isGenerating ? C.muted : '#fff',
                    fontWeight: 700, fontSize: 11.5, cursor: isGenerating || engineOk === false ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    transition: 'opacity .15s',
                    opacity: isGenerating ? 0.7 : 1,
                  }}
                >
                  {isGenerating ? <><Spinner /> Generando…</> : '⚡ Generar'}
                </button>

                {isReady && (
                  <button
                    onClick={() => downloadReport(job)}
                    style={{
                      flex: 1, padding: '8px 0', borderRadius: 7, border: `1px solid ${def.color}`,
                      background: 'transparent', color: def.color,
                      fontWeight: 700, fontSize: 11.5, cursor: 'pointer',
                    }}
                  >
                    ⬇ Descargar
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Recent reports history ── */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            📂 Reportes generados en esta sesión
          </div>
          <button
            onClick={refreshList}
            style={{ background: C.tint, border: `1px solid ${C.border}`, borderRadius: 6, color: C.muted, padding: '4px 10px', fontSize: 10.5, cursor: 'pointer' }}
          >
            ↻ Actualizar
          </button>
        </div>

        {recentList.length === 0 ? (
          <div style={{ color: C.muted, fontSize: 11.5, textAlign: 'center', padding: '20px 0' }}>
            Sin reportes generados aún en esta sesión.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {['Tipo', 'ID', 'Estado', 'Creado', 'Descargar'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '5px 8px', color: C.muted, fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentList.map(r => {
                const def = REPORT_DEFS.find(d => d.id === r.type)
                const statusColor = r.status === 'ready' ? '#22c55e' : r.status === 'error' ? '#ef4444' : '#F89421'
                return (
                  <tr key={r.report_id} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: '7px 8px', color: C.text, fontWeight: 600 }}>
                      {def?.icon} {def?.label ?? r.type}
                    </td>
                    <td style={{ padding: '7px 8px', color: C.muted, fontFamily: 'monospace', fontSize: 10 }}>
                      {r.report_id.slice(0, 8)}…
                    </td>
                    <td style={{ padding: '7px 8px' }}>
                      <span style={{ color: statusColor, fontWeight: 700 }}>{r.status}</span>
                    </td>
                    <td style={{ padding: '7px 8px', color: C.muted }}>
                      {new Date(r.created_at).toLocaleTimeString('es-DO')}
                    </td>
                    <td style={{ padding: '7px 8px' }}>
                      {r.status === 'ready' && (
                        <a
                          href={`${REPORTS_API}/reports/${r.report_id}`}
                          download
                          style={{ color: def?.color ?? C.blue, fontWeight: 700, fontSize: 10.5, textDecoration: 'none' }}
                        >
                          ⬇ PDF
                        </a>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Footer note ── */}
      <div style={{ fontSize: 10, color: C.muted, textAlign: 'center', paddingBottom: 4 }}>
        PDFs generados con Puppeteer + Handlebars · Chromium system · ATMVISION report-engine v0.1.0 ·
        Remitir vía JMBR Portal SIB · Solo para uso interno
      </div>
    </div>
  )
}
